'use client';

import NextLink from 'next/link';
import { useTranslations } from 'next-intl';

import { alpha } from '@mui/material/styles';
import { Box, Link, Stack, Container, Typography } from '@mui/material';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import Logo from 'src/components/logo/Logo';

export function Footer() {
  const t = useTranslations('Footer');

  const COLUMNS: Array<{
    title: string;
    items: Array<{ label: string; href: string; external?: boolean }>;
  }> = [
    {
      title: t('links.testelope'),
      items: [
        { label: t('links.home'), href: '/' },
        { label: t('links.contact'), href: paths.contact },
        { label: t('links.login'), href: paths.auth.login, external: true },
      ],
    },
    {
      title: t('links.legal'),
      items: [
        { label: t('links.terms'), href: '#' },
        { label: t('links.privacy'), href: '#' },
      ],
    },
    {
      title: t('links.contactSection'),
      items: [{ label: t('links.email'), href: 'mailto:support@testelope.com', external: true }],
    },
  ];

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: 'grey.900',
        color: 'common.white',
        borderTop: `1px solid ${alpha('#ffffff', 0.06)}`,
        pt: { xs: 7, md: 10 },
        pb: { xs: 5, md: 6 },
      }}
    >
      <Container>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
              md: '2fr 1fr 1fr 1fr',
            },
            gap: { xs: 4, md: 6 },
            mb: { xs: 5, md: 8 },
          }}
        >
          {/* Brand col */}
          <Box>
            <Stack
              component={NextLink}
              href="/"
              direction="row"
              alignItems="center"
              spacing={1.25}
              sx={{ mb: 2.5, textDecoration: 'none', color: 'inherit' }}
            >
              <Logo disabledLink width={32} height={32} />
              <Typography
                sx={{
                  fontFamily: 'Barlow, sans-serif',
                  fontWeight: 700,
                  fontSize: 16,
                  color: 'common.white',
                }}
              >
                Testelope
              </Typography>
            </Stack>
            <Typography
              sx={{
                fontSize: 14,
                lineHeight: 1.7,
                color: alpha('#ffffff', 0.4),
                maxWidth: 320,
              }}
            >
              {t('description')}
            </Typography>
          </Box>

          {COLUMNS.map((col) => (
            <Box key={col.title}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  textTransform: 'uppercase',
                  color: alpha('#ffffff', 0.5),
                  mb: 2.5,
                }}
              >
                {col.title}
              </Typography>
              <Stack spacing={1.5}>
                {col.items.map((item) =>
                  item.external ? (
                    <Link
                      key={item.label}
                      component={NextLink}
                      href={item.href}
                      underline="none"
                      sx={{
                        fontSize: 14,
                        color: alpha('#ffffff', 0.45),
                        '&:hover': { color: alpha('#ffffff', 0.85) },
                      }}
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <Link
                      key={item.label}
                      component={RouterLink}
                      href={item.href}
                      underline="none"
                      sx={{
                        fontSize: 14,
                        color: alpha('#ffffff', 0.45),
                        '&:hover': { color: alpha('#ffffff', 0.85) },
                      }}
                    >
                      {item.label}
                    </Link>
                  )
                )}
              </Stack>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            pt: 3,
            borderTop: `1px solid ${alpha('#ffffff', 0.06)}`,
            fontSize: 13,
            color: alpha('#ffffff', 0.25),
          }}
        >
          {t('copyright')} Testelope AB
        </Box>
      </Container>
    </Box>
  );
}
